import { createContext, useContext } from 'react';
import { FontWeight, Heading, Text } from '@lili/lili-frontend-common';
import { twMerge } from 'tailwind-merge';
import { FormFooter } from '@Components/happyFlowTemplate/FormFooter.tsx';
import { useBoundStore } from '@Store/useBoundStore';
import { useAsyncBlock } from '@Hooks/utils/useAsyncBlock';
import type { ExtendedUseFormReturn } from '@Hooks/index';
import type { Control, FieldValues, UseFormReturn } from 'react-hook-form';
import type React from 'react';

interface StepPageContextValue {
  isBlocked: boolean;
}

const StepPageContext = createContext<StepPageContextValue>({ isBlocked: false });

export const useStepPageContext = () => useContext(StepPageContext);

interface IStepPageWrapper<T extends FieldValues> {
  heading?: string | React.ReactNode;
  content?: string | React.ReactNode;
  children: React.ReactNode;
  isLoading?: boolean;
  handleSubmit?: () => Promise<unknown | void>;
  formMethods?: ExtendedUseFormReturn<T> | UseFormReturn<T>;
  maxWidth?: number;
  customBackHandling?: () => void;
  customSubmitTitle?: string;
  footerInputSlot?: React.ReactNode;
  shouldStrechCta?: boolean;
  isFormValid?: boolean;
  skipFormValidation?: boolean;
  centerCta?: boolean;
  shouldCenterContent?: boolean;
  subContentColor?: string;
  text?: string;
  textCustomStyle?: string;
  headingContainerCustomStyle?: string;
  showFooter?: boolean;
  showBackButton?: boolean;
  showNextButton?: boolean;
}

export const StepPageWrapper = <T extends FieldValues>({
  heading,
  content,
  children,
  isLoading = false,
  handleSubmit,
  formMethods,
  maxWidth,
  customBackHandling,
  customSubmitTitle,
  footerInputSlot,
  shouldStrechCta,
  isFormValid,
  skipFormValidation,
  centerCta,
  shouldCenterContent,
  subContentColor,
  showFooter = true,
  showBackButton = true,
  showNextButton = true,
  text,
  textCustomStyle,
  headingContainerCustomStyle,
}: IStepPageWrapper<T>) => {
  const isEmbedded = useBoundStore((state) => state.onboardingSlice.getIsEmbedded());
  const { blockingAction, isBlocked } = useAsyncBlock();

  const layoutStyle = twMerge(
    'grow w-full h-full flex justify-center items-start overflow-y-scroll px-[16px] md:px-[24px]',
    isEmbedded ? 'py-[24px] md:pt-[64px]' : 'pb-[40px] pt-[24px] md:pt-[127px]'
  );

  return (
    <StepPageContext.Provider value={{ isBlocked: isLoading || isBlocked }}>
      <div
        className='flex flex-col w-full h-full responsive-content justify-between items-center fade-in overflow-hidden'
        id='page-wrapper'>
        <div className={layoutStyle} id='page-layout'>
          <div
            style={{ maxWidth: maxWidth ? `${maxWidth}px` : '500px' }}
            className='flex flex-col w-full h-full'
            id='page-content'>
            <div
              className={`flex flex-col mb-[40px] ${shouldCenterContent ? 'items-center' : ''} ${headingContainerCustomStyle}`}>
              {heading &&
                (typeof heading === 'string' ? (
                  <Heading.H4 id='stepPageHeader' fontWeight={FontWeight.SEMIBOLD}>
                    {heading}
                  </Heading.H4>
                ) : (
                  heading
                ))}
              {content &&
                (typeof content === 'string' ? (
                  <Heading.H8
                    id='stepPageContent'
                    fontWeight={FontWeight.MEDIUM}
                    className={`${subContentColor || ''} whitespace-pre-line mt-[24px]`}>
                    {content}
                  </Heading.H8>
                ) : (
                  content
                ))}
              {text && (
                <Text.H10
                  className={`text-text-main-secondary mt-[8px] ${textCustomStyle}`}
                  id='stepPageText'>
                  {text}
                </Text.H10>
              )}
            </div>
            {children}
          </div>
        </div>
        {formMethods && showFooter && (
          <FormFooter
            footerInputSlot={footerInputSlot}
            customSubmitTitle={customSubmitTitle}
            formMethods={{ control: formMethods.control as Control<FieldValues> }}
            maxWidth={maxWidth}
            isLoading={isLoading || isBlocked}
            onSubmit={() => (handleSubmit ? blockingAction(handleSubmit) : null)}
            customBackHandling={customBackHandling}
            shouldStrechCta={shouldStrechCta}
            isFormValid={isFormValid}
            skipFormValidation={skipFormValidation}
            centerCta={centerCta}
            showBackButton={showBackButton}
            showNextButton={showNextButton}
          />
        )}
      </div>
    </StepPageContext.Provider>
  );
};
